import React from "react";
import { useTranslation } from "react-i18next";
import Card from "./Card";
import IconButton from "../components/IconButton";
import DeleteIcon from "../../icons/DeleteIcon";
import SettingIcon from "../../icons/SettingIcon";

type MetricCardProps = {
  isCenter: boolean;
  title: string | React.ReactNode;
  value: string;
  delta?: string;
  deltaTone?: "up" | "down";
  isLoading?: boolean;
  actions?: Array<{
    label: string;
    onClick?: (event) => void;
    content?: React.ReactNode;
  }>;
  icon?: React.ReactNode;
  compareText?: string;
  color?: string;
  onSetting?: (event) => void;
  onDelete?: (event) => void;
};

const MetricCard = ({
  isCenter,
  title,
  value,
  delta,
  deltaTone = "up",
  isLoading = false,
  actions = [],
  icon,
  compareText = "vs. last month",
  color,
  onSetting,
  onDelete,
}: MetricCardProps) => {
  const { t } = useTranslation();

  const deltaColor = deltaTone === "up" ? "text-teal-500" : "text-red-500";

  return (
    <Card className="p-5 group">
      {isLoading ? (
        <div className="space-y-2">
          <div className="animate-pulse h-4 bg-gray-100 rounded w-1/2" />
          <div className="animate-pulse h-7 bg-gray-100 rounded w-2/3" />
          <div className="animate-pulse h-3 bg-gray-100 rounded w-1/3" />
        </div>
      ) : (
        <>
          <div className="flex flex-row justify-between items-start gap-4">
            <div className={`w-full flex items-center ${isCenter ? "justify-center" : ""}`}>
              <div>
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: color }}
                >
                  <div className="text-white">
                    {React.isValidElement(icon)
                      ? React.cloneElement(icon as any, {
                          className: `${(icon as any).props?.className || ""} text-white`,
                          color: "#fff",
                        })
                      : icon}
                  </div>
                </div>
                {typeof value === "string" ? (
                  <div className="text-gray-600 text-sm md:text-base font-medium mt-4 tracking-tight">
                    {title}
                  </div>
                ) : (
                  <div className="text-gray-600 text-sm md:text-base font-medium mt-4 tracking-tight -ml-3">
                    {title}
                  </div>
                )}
                <div className="mt-1.5 font-semibold text-[28px] leading-8 tracking-tight text-gray-900">
                  {value}
                </div>
                <div className="mt-3">
                  <div className="text-sm flex items-center gap-2">
                    <span className={deltaColor + " font-medium"}>
                      {delta && String(delta).trim() ? delta : "0%"}
                    </span>
                    <span className="text-gray-500"> {compareText}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex gap-2 items-center opacity-0 group-hover:opacity-100 transition-opacity">
              {actions.map((a, idx) => (
                <IconButton
                  key={idx}
                  label={a.label}
                  className="cursor-pointer"
                  onClick={(event) => a.onClick(event)}
                >
                  {a.content || <span>⋯</span>}
                </IconButton>
              ))}
              {onSetting && (
                <IconButton
                  label={t("Settings")}
                  className="cursor-pointer"
                  onClick={(event) => onSetting(event)}
                >
                  <SettingIcon />
                </IconButton>
              )}
              {onDelete && (
                <IconButton
                  label={t("Delete")}
                  className="cursor-pointer"
                  onClick={(event) => onDelete(event)}
                >
                  <DeleteIcon />
                </IconButton>
              )}
              {/* <IconButton label={t("Move")}>
                <MoveIcon />
              </IconButton> */}
            </div>
          </div>
        </>
      )}
    </Card>
  );
};

export default MetricCard;
